<template>
    <AdminLayout>
        <SpinnerBlock v-if="isSearching" />

        <!-- Server Status Dashboard -->
        <div class="server-dashboard">
            <div class="server-stats">
                <div class="stat-card total">
                    <div class="stat-label">Total Servers</div>
                    <div class="stat-value">{{ serverStats.total }}</div>
                </div>
                <div class="stat-card success" @click="filterByStatus('success')">
                    <div class="stat-label">Responded</div>
                    <div class="stat-value">{{ serverStats.responded }}</div>
                </div>
                <div class="stat-card failed" @click="filterByStatus('failed')">
                    <div class="stat-label">Failed</div>
                    <div class="stat-value">{{ serverStats.failed }}</div>
                </div>
                <div class="stat-card timeout" @click="filterByStatus('timeout')">
                    <div class="stat-label">Timeout</div>
                    <div class="stat-value">{{ serverStats.timeout }}</div>
                </div>
            </div>

            <!-- Server Status Cards -->
            <div class="server-grid">
                <div v-for="server in serverStatuses" :key="server.name" class="server-card" :class="server.status"
                    @click="filterByServer(server.name)">
                    <div class="server-header">
                        <span class="server-name">{{ server.name }}</span>
                        <span class="server-status-badge">{{ server.status }}</span>
                    </div>
                    <div class="server-body">
                        <div class="server-info">
                            <span class="info-label">Response Time:</span>
                            <span class="info-value" :class="server.responseTimeClass">
                                {{ server.responseTime }}ms
                            </span>
                        </div>
                        <div class="server-info">
                            <span class="info-label">Active Clients:</span>
                            <span class="info-value">{{ server.clientCount }}</span>
                        </div>
                        <div class="server-info" v-if="server.error">
                            <span class="info-label">Error:</span>
                            <span class="info-value error-text">{{ server.error }}</span>
                        </div>
                    </div>
                    <div class="server-footer">
                        <div class="progress-bar">
                            <div class="progress-fill" :style="{ width: (server.clientCount / 500) * 100 + '%' }"
                                :class="server?.status?.toLowerCase() || 'timeout'">
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Filter Controls -->
            <div class="filter-controls">
                <!-- Search by Client Name -->
                <div class="search-box">
                    <input type="text" v-model="searchQuery" placeholder="Search by client name..." class="search-input"
                        @input="handleSearchInput" />
                    <button v-if="searchQuery" @click="clearSearch" class="clear-search">
                        <i class="fas fa-times"></i>
                    </button>
                </div>

                <div class="filter-buttons">
                    <button class="filter-btn" :class="{ active: currentFilter === 'all' }" @click="setFilter('all')">
                        All Clients ({{ filteredClients.length }})
                    </button>
                    <button class="filter-btn" :class="{ active: currentFilter === 'success' }"
                        @click="setFilter('success')">
                        From Responded Servers
                    </button>
                    <button class="filter-btn" :class="{ active: currentFilter === 'server' }" v-if="selectedServer"
                        @click="clearServerFilter">
                        Server: {{ selectedServer }} ✕
                    </button>
                </div>
            </div>

            <!-- Active Filters Display -->
            <div class="active-filters" v-if="hasActiveFilters">
                <span class="filter-label">Active Filters:</span>
                <span class="filter-tag" v-if="searchQuery">
                    Search: "{{ searchQuery }}"
                    <i class="bi bi-x" @click="clearSearch"></i>
                </span>
                <span class="filter-tag" v-if="currentFilter === 'success'">
                    Responded Servers Only
                    <i class="fas fa-times" @click="setFilter('all')"></i>
                </span>
                <span class="filter-tag" v-if="selectedServer">
                    Server: {{ selectedServer }}
                    <i class="fas fa-times" @click="clearServerFilter"></i>
                </span>
                <button v-if="hasActiveFilters" class="clear-all-btn" @click="clearAllFilters">
                    Clear All
                </button>
            </div>
        </div>

        <!-- Clients Table with Server Indicators -->
        <div class="table-container">
            <div class="table-header">
                <div class="results-info">
                    Showing {{ filteredClients.length }} of {{ clients.length }} clients
                </div>
            </div>
            <table class="table table-sm table-hover">
                <thead>
                    <tr>
                        <th>Client Name</th>
                        <th>Uptime</th>
                        <th>Server</th>
                        <th>Response Time</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="client in filteredClients" :key="client.name + client.server"
                        :class="getClientRowClass(client)">
                        <td>
                            <span class="client-name">{{ client.name }}</span>
                        </td>
                        <td>{{ client.upTime }}</td>
                        <td>
                            <span class="server-badge" :class="getServerStatusClass(client.server)">
                                {{ client.server }}
                                <span class="response-time" v-if="client.responseTime">
                                    ({{ client.responseTime }}ms)
                                </span>
                            </span>
                        </td>
                        <td>
                            <span class="response-badge" :class="getResponseTimeClass(client.responseTime)">
                                {{ client.responseTime || 'N/A' }}ms
                            </span>
                        </td>
                        <td>
                            <span class="status-indicator" :class="client.status?.toLowerCase()">
                                {{ client.status === 'Ok' ? 'LIVE' : 'CACHED' || 'active' }}
                            </span>
                            <button @click="handleRemoveClick(client)" class="btn btn-xs"><i
                                    class="bi bi-trash"></i></button>
                        </td>
                    </tr>
                    <tr v-if="filteredClients.length === 0">
                        <td colspan="5" class="text-center no-data">
                            No clients found matching your filters
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </AdminLayout>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue';
import SpinnerBlock from '@/components/animations/SpinnerBlock.vue';
import AdminLayout from '@/layouts/AdminLayout.vue';
import clientService from '@/services/clientService';
import confirmationService from '@/services/confirmationService';
import toastService from '@/services/toastService';
import spinnerService from '@/services/spinnerService';

const clients = ref([]);
const serverStatuses = ref([]);
const isSearching = ref(false);
const currentFilter = ref('all');
const selectedServer = ref(null);
const searchQuery = ref('');
const searchTimeout = ref(null);
let intervalId = null;

// Server statistics
const serverStats = computed(() => {
    const stats = {
        total: serverStatuses.value.length,
        responded: 0,
        failed: 0,
        timeout: 0
    };

    serverStatuses.value.forEach(server => {
        if (server.status === 'Ok') stats.responded++;
        else if (server.status === 'Failed') stats.failed++;
        else if (server.status === 'timeout') stats.timeout++;
    });

    return stats;
});

// Check if any filters are active
const hasActiveFilters = computed(() => {
    return searchQuery.value || currentFilter.value === 'success' || selectedServer.value;
});

// Filter clients based on current filter and search query
const filteredClients = computed(() => {
    let filtered = clients.value;

    // Apply server filter
    if (selectedServer.value) {
        filtered = filtered.filter(c => c.server === selectedServer.value);
    } else if (currentFilter.value === 'success') {
        const successfulServers = serverStatuses.value
            .filter(s => s.status === 'Ok')
            .map(s => s.name);
        filtered = filtered.filter(c => successfulServers.includes(c.server));
    }

    // Apply search filter (case-insensitive)
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase().trim();
        filtered = filtered.filter(client =>
            client.name.toLowerCase().includes(query)
        );
    }

    return filtered;
});

const search = async () => {
    if (isSearching.value)
        return;
    isSearching.value = true;
    try {
        const response = await clientService.getMTActiveClients();

        // Process the response to extract server statuses
        clients.value = response.clients || [];
        serverStatuses.value = response.serverStatuses || [];

        // Add response time and status to each client
        clients.value = clients.value.map(client => {
            const serverInfo = serverStatuses.value.find(s => s.name === client.server);
            return {
                ...client,
                responseTime: serverInfo?.responseTime || null,
                status: serverInfo?.status || 'unknown'
            };
        });

    } catch (error) {
        console.error('Failed to fetch clients:', error);
    } finally {
        isSearching.value = false;
    }
};

const setFilter = (filter) => {
    currentFilter.value = filter;
    selectedServer.value = null;
};

const filterByServer = (serverName) => {
    selectedServer.value = serverName;
    currentFilter.value = 'server';
};

const clearServerFilter = () => {
    selectedServer.value = null;
    currentFilter.value = 'all';
};

// Search with debounce to avoid too many updates while typing
const handleSearchInput = () => {
    // Clear existing timeout
    if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
    }

    // Set new timeout for debounce
    searchTimeout.value = setTimeout(() => {
        // Search is already reactive through v-model
        console.log('Searching for:', searchQuery.value);
    }, 300); // 300ms debounce
};

const clearSearch = () => {
    searchQuery.value = '';
};

const clearAllFilters = () => {
    searchQuery.value = '';
    currentFilter.value = 'all';
    selectedServer.value = null;
};

const getServerStatusClass = (serverName) => {
    const server = serverStatuses.value.find(s => s.name === serverName);
    return server?.status || 'unknown';
};

const getResponseTimeClass = (time) => {
    if (!time) return 'no-response';
    if (time < 100) return 'fast';
    if (time < 300) return 'medium';
    return 'slow';
};

const getClientRowClass = (client) => {
    return {
        'server-success': client.status?.toLowerCase() === 'ok',
        'server-failed': client.status?.toLowerCase() === 'failed',
        'server-timeout': client.status?.toLowerCase() === 'timeout'
    };
};

const handleRemoveClick = async (client) => {
    const confirmed = await confirmationService.show(`Are you sure you want to disconnect this ${client.name}?`);
    if (!confirmed) return;
    spinnerService.show();
    const resp = await clientService.removeFromActive(client.id, client.server);
    if (resp.success) {
        toastService.success("Client removed!");
        clients.value = clients.value.filter(x => !(x.id === client.id && x.server === client.server));
    }
    else {
        toastService.error(resp.message);
    }
    spinnerService.hide();
}

onMounted(() => {
    search();
    // Set up interval (30 seconds = 30000 milliseconds)
    intervalId = setInterval(() => {
        search();
    }, 30000);
});

onUnmounted(() => {
    // Clean up interval when component is destroyed
    if (intervalId) {
        clearInterval(intervalId);
    }
    // Clean up search timeout
    if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
    }
});
</script>


<style scoped>
.search-box {
    position: relative;
    flex: 1;
    max-width: 300px;
}

.search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    font-size: 14px;
}

.search-input {
    width: 100%;
    padding: 10px 35px 10px 35px;
    border: 1px solid #dee2e6;
    border-radius: 20px;
    font-size: 14px;
    transition: all 0.2s;
}

.search-input:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.clear-search {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #6c757d;
    cursor: pointer;
    padding: 0;
    font-size: 14px;
}

.clear-search:hover {
    color: #dc3545;
}

.filter-controls {
    display: flex;
    gap: 15px;
    margin: 20px 0;
    flex-wrap: wrap;
    align-items: center;
}

.filter-buttons {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.active-filters {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px;
    background: #e9ecef;
    border-radius: 8px;
    margin-top: 10px;
    flex-wrap: wrap;
}

.filter-label {
    font-size: 12px;
    font-weight: 600;
    color: #495057;
    text-transform: uppercase;
}

.filter-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 10px;
    background: #007bff;
    color: white;
    border-radius: 20px;
    font-size: 12px;
}

.filter-tag i {
    cursor: pointer;
    font-size: 10px;
}

.filter-tag i:hover {
    opacity: 0.8;
}

.clear-all-btn {
    padding: 5px 10px;
    background: #6c757d;
    color: white;
    border: none;
    border-radius: 20px;
    font-size: 12px;
    cursor: pointer;
    margin-left: auto;
}

.clear-all-btn:hover {
    background: #5a6268;
}

.table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
}

.results-info {
    font-size: 14px;
    color: #6c757d;
}

/* Highlight matching text in search results */
.client-name {
    position: relative;
}

.client-name mark {
    background: #fff3cd;
    padding: 0;
    color: inherit;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .filter-controls {
        flex-direction: column;
        align-items: stretch;
    }

    .search-box {
        max-width: 100%;
    }

    .filter-buttons {
        justify-content: center;
    }

    .active-filters {
        flex-direction: column;
        align-items: flex-start;
    }

    .clear-all-btn {
        margin-left: 0;
        width: 100%;
    }
}

.server-dashboard {
    padding: 20px;
    background: #f8f9fa;
    border-radius: 10px;
    margin-bottom: 20px;
}

.server-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 15px;
    margin-bottom: 30px;
}

.stat-card {
    background: white;
    padding: 15px;
    border-radius: 8px;
    text-align: center;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.stat-card.total {
    border-left: 4px solid #6c757d;
}

.stat-card.success {
    border-left: 4px solid #28a745;
}

.stat-card.failed {
    border-left: 4px solid #dc3545;
}

.stat-card.timeout {
    border-left: 4px solid #ffc107;
}

.stat-label {
    font-size: 12px;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.stat-value {
    font-size: 24px;
    font-weight: bold;
    margin-top: 5px;
}

.server-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 15px;
    margin-bottom: 20px;
}

.server-card {
    background: white;
    border-radius: 8px;
    padding: 15px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid #dee2e6;
}

.server-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.server-card.success {
    border-left: 4px solid #28a745;
}

.server-card.failed {
    border-left: 4px solid #dc3545;
}

.server-card.timeout {
    border-left: 4px solid #ffc107;
}

.server-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.server-name {
    font-weight: 600;
    font-size: 16px;
}

.server-status-badge {
    font-size: 11px;
    padding: 3px 8px;
    border-radius: 12px;
    text-transform: uppercase;
    font-weight: 600;
}

.success .server-status-badge {
    background: #28a745;
    color: white;
}

.failed .server-status-badge {
    background: #dc3545;
    color: white;
}

.timeout .server-status-badge {
    background: #ffc107;
    color: #212529;
}

.server-info {
    display: flex;
    justify-content: space-between;
    margin-bottom: 5px;
    font-size: 13px;
}

.info-label {
    color: #6c757d;
}

.info-value {
    font-weight: 600;
}

.info-value.fast {
    color: #28a745;
}

.info-value.medium {
    color: #ffc107;
}

.info-value.slow {
    color: #fd7e14;
}

.error-text {
    color: #dc3545;
}

.server-footer {
    margin-top: 10px;
}

.progress-bar {
    height: 4px;
    background: #e9ecef;
    border-radius: 2px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    transition: width 0.3s ease;
}

.progress-fill.ok {
    background: #28a745;
}

.progress-fill.failed {
    background: #dc3545;
}

.progress-fill.timeout {
    background: #ffc107;
}

.filter-controls {
    display: flex;
    gap: 10px;
    margin: 20px 0;
    flex-wrap: wrap;
}

.filter-btn {
    padding: 8px 16px;
    border: 1px solid #dee2e6;
    background: white;
    border-radius: 20px;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.2s;
}

.filter-btn:hover {
    background: #e9ecef;
}

.filter-btn.active {
    background: #007bff;
    color: white;
    border-color: #007bff;
}

.table-container {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table {
    width: 100%;
    border-collapse: collapse;
}

.table th {
    text-align: left;
    padding: 12px;
    background: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
    color: #495057;
}

.table td {
    padding: 12px;
    border-bottom: 1px solid #dee2e6;
}

.table tbody tr:hover {
    background: #f8f9fa;
}

.table tbody tr.server-success {
    border-left: 3px solid #28a745;
}

.table tbody tr.server-failed {
    border-left: 3px solid #dc3545;
    opacity: 0.7;
}

.table tbody tr.server-timeout {
    border-left: 3px solid #ffc107;
    opacity: 0.7;
}

.server-badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
}

.server-badge.success {
    background: #d4edda;
    color: #155724;
}

.server-badge.failed {
    background: #f8d7da;
    color: #721c24;
}

.server-badge.timeout {
    background: #fff3cd;
    color: #856404;
}

.response-time {
    font-size: 11px;
    opacity: 0.8;
}

.response-badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 500;
}

.response-badge.fast {
    background: #d4edda;
    color: #155724;
}

.response-badge.medium {
    background: #fff3cd;
    color: #856404;
}

.response-badge.slow {
    background: #f8d7da;
    color: #721c24;
}

.response-badge.no-response {
    background: #e9ecef;
    color: #6c757d;
}

.status-indicator {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
}

.status-indicator.ok {
    background: #d4edda;
    color: #155724;
}

.status-indicator.failed {
    background: #f8d7da;
    color: #721c24;
}

.no-data {
    padding: 30px;
    color: #6c757d;
    font-style: italic;
}

@media (max-width: 768px) {
    .server-grid {
        grid-template-columns: 1fr;
    }

    .server-stats {
        grid-template-columns: 1fr 1fr;
    }

    .table {
        font-size: 12px;
    }

    .table td,
    .table th {
        padding: 8px 4px;
    }
}
</style>